import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, hasCapability } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, RotateCcw, Save, Lock, AlertTriangle } from "lucide-react";

const isEqualSet = (a, b) => {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
};

export default function RolesPermissionsEdit() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState({}); // {cap_key: [role,...]}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/permissions-matrix").then(({ data }) => {
      setData(data);
      if (!data.can_edit) {
        toast.error("You don't have permission to edit the matrix.");
        navigate("/roles-permissions");
      }
    }).catch(() => toast.error("Failed to load permissions matrix"));
  }, [navigate]);

  const rolesInOrder = data?.roles || [];

  const currentRoles = (cap) =>
    (pending[cap.key] !== undefined ? pending[cap.key] : cap.roles) || [];

  const isDirty = (cap) => {
    const cur = currentRoles(cap);
    return !isEqualSet(cur, cap.roles);
  };

  const dirtyCount = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    for (const m of data.modules) for (const c of m.capabilities) if (isDirty(c)) n++;
    return n;
  }, [data, pending]);

  const toggle = (cap, role) => {
    if ((cap.locked_roles || []).includes(role)) {
      toast.error(`${ROLE_LABELS[role]} cannot be unlinked from '${cap.label}' — critical guardrail.`);
      return;
    }
    const cur = new Set(currentRoles(cap));
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    setPending((p) => ({ ...p, [cap.key]: Array.from(cur) }));
  };

  const resetCap = (cap) => {
    setPending((p) => ({ ...p, [cap.key]: [...cap.defaults] }));
  };

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const matrix = {};
      for (const m of data.modules) {
        for (const c of m.capabilities) {
          if (isDirty(c)) matrix[c.key] = currentRoles(c);
        }
      }
      const { data: res } = await api.patch("/permissions-matrix", { matrix });
      toast.success(`Updated ${res.capabilities_updated} capabilit${res.capabilities_updated === 1 ? "y" : "ies"}. Changes are live.`);
      const fresh = await api.get("/permissions-matrix");
      setData(fresh.data);
      setPending({});
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset every capability to its factory default? Any overrides will be lost.")) return;
    setSaving(true);
    try {
      await api.post("/permissions-matrix/reset");
      toast.success("Matrix reset to factory defaults.");
      const fresh = await api.get("/permissions-matrix");
      setData(fresh.data);
      setPending({});
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="text-slate-400 text-center py-12">Loading permissions matrix...</div>;

  return (
    <div className="space-y-6" data-testid="permissions-edit-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#7C1D3A]" /> Roles &amp; Permissions
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Tick the box to grant a role the capability. Changes apply immediately across the app.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/roles-permissions")} data-testid="view-print-matrix-btn">
            Print / Read-only view
          </Button>
          <Button variant="outline" onClick={resetAll} disabled={saving} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reset-all-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset all to defaults
          </Button>
          <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" disabled={saving || dirtyCount === 0} onClick={save} data-testid="save-matrix-btn">
            <Save className="w-4 h-4 mr-1.5" /> Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
          </Button>
        </div>
      </div>

      {dirtyCount > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> You have {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}. Nothing is applied until you click Save.
        </div>
      )}

      {data.modules.map((module) => (
        <Card key={module.name}>
          <CardHeader>
            <CardTitle className="font-heading text-base">{module.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 min-w-[260px]">Capability</th>
                  {rolesInOrder.map((r) => (
                    <th key={r} className="px-1 py-3 text-center font-semibold text-slate-500 whitespace-nowrap min-w-[80px]" title={ROLE_LABELS[r]}>
                      {ROLE_LABELS[r].split(" ").map((w, i) => <div key={i} className="text-[10px] leading-tight">{w}</div>)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {module.capabilities.map((cap) => {
                  const dirty = isDirty(cap);
                  return (
                    <tr key={cap.key} className={dirty ? "bg-amber-50/40" : "hover:bg-slate-50/50"}>
                      <td className="px-4 py-3">
                        <div className="text-slate-900 font-medium">{cap.label}</div>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">{cap.key}</div>
                        <div className="flex items-center gap-2 mt-1">
                          {dirty && (
                            <>
                              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[9px] font-normal">Unsaved</Badge>
                              <button
                                type="button"
                                className="text-[10px] text-[#7C1D3A] hover:underline"
                                onClick={() => resetCap(cap)}
                              >
                                revert to default
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      {rolesInOrder.map((role) => {
                        const set = new Set(currentRoles(cap));
                        const isChecked = set.has(role);
                        const isLocked = (cap.locked_roles || []).includes(role);
                        return (
                          <td key={role} className="px-1 py-2 text-center align-middle">
                            <label className="inline-flex items-center justify-center relative cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isLocked}
                                onChange={() => toggle(cap, role)}
                                className="w-4 h-4 accent-[#7C1D3A] cursor-pointer disabled:cursor-not-allowed"
                                data-testid={`perm-${cap.key}-${role}`}
                              />
                              {isLocked && (
                                <Lock className="w-3 h-3 text-slate-400 absolute -right-4" title="Locked (cannot be un-ticked)" />
                              )}
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      <div className="text-[11px] text-slate-500">
        <b>Guardrails:</b> Locked rows (padlock icon) cannot be un-ticked — this prevents admin lockout on critical capabilities like managing users, company settings and this very matrix.
        <br />
        <b>Live effect:</b> Once saved, users may need to refresh their page to see UI changes; API access is enforced immediately.
      </div>
    </div>
  );
}
